import React, { useEffect } from 'react';

interface AboutModalProps {
  open: boolean;
  onClose: () => void;
}

// Explainer modal for the explorer landing page. Always dismissible — unlike
// the basemap picker, this modal never blocks first-visit interaction. Triggered
// from the "What is this? →" link in the explorer's left-panel framing block.
export const AboutModal: React.FC<AboutModalProps> = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] overflow-y-auto bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="About this dashboard"
      onClick={onClose}
    >
      <div className="min-h-full flex items-center justify-center px-4 py-6">
        <div
          className="relative w-full max-w-2xl bg-figma-panel rounded-figma-panel shadow-figma-card border border-figma-lime/20 p-6 md:p-8"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            aria-label="Close about dialog"
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-figma-text hover:bg-figma-card transition-smooth"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>

          <h2 className="font-roboto font-bold text-title-lg text-figma-text">
            About this dashboard
          </h2>

          <div className="mt-5 space-y-5 font-roboto text-body text-figma-ink-soft leading-relaxed">
            <p>
              A proof-of-concept built by <span className="text-figma-text font-semibold">CTrees</span> in
              partnership with the <span className="text-figma-text font-semibold">Sustainable Forestry
              Initiative (SFI)</span>, prepared for the SFI annual conference. It maps forest data within
              supply sheds around 10 hypothetical US mill locations.
            </p>

            <section>
              <h3 className="font-roboto font-bold text-figma-text text-[15px] mb-1.5">
                What you can do
              </h3>
              <p>
                Pick a mill to explore 12 layers of forest data — biomass, species composition, stand
                density, fire risk, ownership, per-tree attributes, and more. Or run one of three
                <span className="text-figma-yellow"> guided scenarios</span> that walk the layers with a
                specific question in mind.
              </p>
            </section>

            <section>
              <h3 className="font-roboto font-bold text-figma-text text-[15px] mb-1.5">
                What's a supply shed?
              </h3>
              <p>
                The area where a mill sources its wood. Here, each shed is a 60- or 90-minute drive-time
                area around the mill, plus a 3 km buffer along forest roads — a closer approximation to
                actual fibre flow than a simple radius.
              </p>
            </section>

            <section>
              <h3 className="font-roboto font-bold text-figma-text text-[15px] mb-1.5">
                Demo notes
              </h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>This is a working demo, not a production tool. Cards marked
                  <span className="ml-1 px-1.5 py-0.5 rounded bg-figma-yellow/20 text-figma-yellow text-[11px] font-bold tracking-wider uppercase">Pilot preview</span> use stub or limited-coverage data.
                </li>
                <li>
                  The Per-Tree Forest Attributes layer currently covers only the California Sierra
                  Nevada 60-minute shed. Other layers cover all 10 mills.
                </li>
                <li>
                  Color cues:
                  <span className="ml-1 inline-flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-figma-lime" aria-hidden="true" />
                    <span className="text-figma-text">lime</span>
                  </span>
                  &nbsp;= brand accent,
                  <span className="ml-1 inline-flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-figma-mint" aria-hidden="true" />
                    <span className="text-figma-text">mint</span>
                  </span>
                  &nbsp;= controls,
                  <span className="ml-1 inline-flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-figma-yellow" aria-hidden="true" />
                    <span className="text-figma-text">yellow</span>
                  </span>
                  &nbsp;= guided scenarios &amp; pilot data.
                </li>
              </ul>
            </section>

            <section>
              <h3 className="font-roboto font-bold text-figma-text text-[15px] mb-1.5">
                Data sources
              </h3>
              <ul className="text-sm text-figma-ink-mute space-y-1 list-none">
                <li>
                  <span className="text-figma-text font-semibold">CTrees</span>
                  &nbsp;— AGB 100m, Soil Carbon, Growth Projections, LUCA
                  monthly tree-cover-loss, 5m Canopy Height (via Earthscale),
                  Forest Resilience Priority, and the Per-Tree Forest
                  Attributes pilot (CTrees / TreeMap / NSVB pipeline).
                </li>
                <li>
                  <span className="text-figma-text font-semibold">USDA Forest Service</span>
                  &nbsp;— BIGMAP species composition, Forest Ownership
                  (RDS-2025-0045), Wildfire Hazard Potential (RDS-2020-0016-2).
                </li>
                <li>
                  <span className="text-figma-text font-semibold">University of Maine</span>
                  &nbsp;— Relative Density (Stand Density).
                </li>
                <li>
                  <span className="text-figma-text font-semibold">NatureServe</span>
                  &nbsp;— Map of Biodiversity Importance (MoBI) 2024.
                </li>
              </ul>
              <p className="mt-2 text-xs text-figma-ink-mute">
                Full citations in each layer panel.
              </p>
            </section>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 h-10 rounded-figma-button bg-figma-mint text-figma-panel font-roboto font-bold text-sm uppercase tracking-wider hover:bg-figma-mint/90 transition-smooth"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
